import React from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  HStack,
  Icon,
  useColorModeValue,
  Badge,
  Grid,
  SimpleGrid,
  Stack,
  Image,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { demoConfig } from '../config/demoConfig';
import {
  FaUsers,
  FaCheck,
  FaPlay,
  FaDownload,
  FaCalendarAlt,
  FaComments,
  FaMoneyBillWave,
  FaChartLine,
  FaBell,
  FaCog,
  FaPlus,
  FaSignInAlt,
} from 'react-icons/fa';

const FEATURES = [
  {
    id: 'createClub',
    icon: FaPlus,
    colorScheme: 'purple',
    image: '/images/clubApp/create-club.png',
    features: ['customSettings', 'memberTools', 'revenueTracking'],
    featureIcons: [FaCog, FaUsers, FaMoneyBillWave],
  },
  {
    id: 'joinClub',
    icon: FaSignInAlt,
    colorScheme: 'teal',
    image: '/images/clubApp/join-club.png',
    features: ['browseClubs', 'instantAccess', 'joinEvents'],
    featureIcons: [FaUsers, FaComments, FaCalendarAlt],
  },
  {
    id: 'clubLobby',
    icon: FaUsers,
    colorScheme: 'blue',
    image: '/images/clubApp/lobby-clubs.png',
    features: ['eventsOverview', 'memberCount', 'notifications'],
    featureIcons: [FaCalendarAlt, FaUsers, FaBell],
  },
  {
    id: 'profileManagement',
    icon: FaCog,
    colorScheme: 'green',
    image: '/images/clubApp/profile.png',
    features: ['statistics', 'transactionHistory', 'socialConnections'],
    featureIcons: [FaChartLine, FaMoneyBillWave, FaComments],
  },
];

const MobileFeatureCard: React.FC<{
  feature: (typeof FEATURES)[0];
  t: any;
  useColorModeValue: any;
}> = ({ feature, t, useColorModeValue }) => {
  const { id, image, featureIcons, features, colorScheme } = feature;

  return (
    <Box minW='85%' maxW='85%' scrollSnapAlign='center'>
      <VStack mb={3} spacing={2} align='start'>
        <Heading
          size='sm'
          color={useColorModeValue('gray.800', 'white')}
          wordBreak='break-word'
        >
          {t(`clubAppPage.appFeatures.${id}.title`)}
        </Heading>
        <Text fontSize='xs' color='gray.500' wordBreak='break-word'>
          {t(`clubAppPage.appFeatures.${id}.subtitle`)}
        </Text>
        <VStack spacing={1} align='start' w='full'>
          {features.map((featureKey, index) => (
            <HStack key={featureKey} spacing={2} align='start'>
              <Icon
                as={featureIcons[index]}
                color={`${colorScheme}.500`}
                boxSize={3}
                flexShrink={0}
                mt={0.5}
              />
              <Text
                fontSize='xs'
                color={useColorModeValue('gray.700', 'gray.300')}
                wordBreak='break-word'
                flex={1}
              >
                {t(`clubAppPage.appFeatures.${id}.features.${featureKey}`)}
              </Text>
            </HStack>
          ))}
        </VStack>
      </VStack>
      <Box position='relative' overflow='hidden'>
        <Image
          src={image}
          alt={`${t(`clubAppPage.appFeatures.${id}.title`)} Interface`}
          h='520px'
          objectFit='contain'
          borderRadius='xl'
        />
      </Box>
    </Box>
  );
};

const ClubAppPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box
        py={{ base: 12, md: 20 }}
        bg={useColorModeValue('gray.50', 'gray.900')}
      >
        <Container
          display='flex'
          flexDirection='column'
          alignItems='center'
          maxW='7xl'
        >
          <VStack
            spacing={4}
            textAlign='center'
            mb={{ base: 8, md: 12, lg: 16 }}
          >
            <Badge
              colorScheme='orange'
              variant='subtle'
              px={4}
              py={2}
              borderRadius='full'
              fontSize='sm'
              fontWeight='bold'
            >
              {t('clubAppPage.badge')}
            </Badge>
            <Heading
              size={{ base: 'xl', md: '2xl' }}
              color={useColorModeValue('gray.800', 'white')}
              wordBreak='break-word'
            >
              {t('clubAppPage.title')}
            </Heading>
            <Text
              fontSize='lg'
              color='gray.500'
              maxW='2xl'
              wordBreak='break-word'
            >
              {t('clubAppPage.subtitle')}
            </Text>
          </VStack>

          <VStack spacing={16}>
            <Grid
              templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
              gap={{ base: 6, md: 8, lg: 12 }}
              alignItems='center'
              w='full'
            >
              <Box
                bg={useColorModeValue('white', 'gray.800')}
                borderRadius='2xl'
                p={{ base: 6, md: 8 }}
                boxShadow='xl'
                border='1px solid'
                borderColor={useColorModeValue('gray.200', 'gray.700')}
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: '2xl',
                }}
                transition='all 0.3s ease'
                maxW={{ base: '100%', lg: '480px' }}
                w='full'
              >
                <VStack align='start' spacing={{ base: 4, md: 6 }}>
                  <VStack align='start' spacing={{ base: 3, md: 4 }}>
                    <HStack spacing={{ base: 3, md: 4 }}>
                      <Box
                        p={4}
                        bg={useColorModeValue('orange.100', 'orange.200')}
                        borderRadius='2xl'
                        color={useColorModeValue('orange.600', 'orange.800')}
                      >
                        <Icon as={FaUsers} boxSize={{ base: 8, md: 10 }} />
                      </Box>
                      <VStack align='start' spacing={2}>
                        <Heading
                          size={{ base: 'lg', md: 'xl' }}
                          color={useColorModeValue('gray.800', 'white')}
                          fontWeight='800'
                          wordBreak='break-word'
                        >
                          {t('clubAppPage.platform.title')}
                        </Heading>
                        <HStack spacing={{ base: 1, md: 2 }} flexWrap='wrap'>
                          <Badge
                            colorScheme='orange'
                            variant='solid'
                            borderRadius='full'
                            whiteSpace='normal'
                            textAlign='center'
                          >
                            {t('clubAppPage.platform.badges.allInOne')}
                          </Badge>
                          <Badge
                            colorScheme='green'
                            variant='outline'
                            borderRadius='full'
                            whiteSpace='normal'
                            textAlign='center'
                          >
                            {t('clubAppPage.platform.badges.communityFocused')}
                          </Badge>
                        </HStack>
                      </VStack>
                    </HStack>

                    <Text
                      fontSize={{ base: 'md', md: 'lg' }}
                      color={useColorModeValue('gray.600', 'gray.300')}
                      lineHeight='tall'
                      wordBreak='break-word'
                    >
                      {t('clubAppPage.platform.description')}
                    </Text>
                  </VStack>
                  {/* Images inside card - only on mobile */}
                  <HStack
                    spacing={4}
                    w='full'
                    mt={4}
                    display={{ base: 'flex', lg: 'none' }}
                    justify='center'
                  >
                    <Box
                      borderRadius='xl'
                      overflow='hidden'
                      flex={1}
                      maxW='200px'
                    >
                      <Image
                        src='/images/clubApp/club.png'
                        alt='ClubApp Main Interface'
                        w='full'
                        h='auto'
                        objectFit='contain'
                      />
                    </Box>
                    <Box
                      borderRadius='xl'
                      overflow='hidden'
                      flex={1}
                      maxW='200px'
                    >
                      <Image
                        src='/images/clubApp/club-lobby-filter.png'
                        alt='ClubApp Lobby Filter'
                        w='full'
                        h='auto'
                        objectFit='contain'
                      />
                    </Box>
                  </HStack>
                  <VStack align='start' spacing={4} w='full'>
                    <Heading
                      size='md'
                      color={useColorModeValue('gray.800', 'white')}
                      fontWeight='700'
                      wordBreak='break-word'
                    >
                      {t('clubAppPage.platform.featuresTitle')}
                    </Heading>
                    <SimpleGrid
                      columns={2}
                      spacing={{ base: 2, md: 3 }}
                      w='full'
                    >
                      <HStack spacing={3} align='start'>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                          flexShrink={0}
                          mt={0.5}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                          wordBreak='break-word'
                          flex={1}
                        >
                          {t('clubAppPage.platform.features.memberManagement')}
                        </Text>
                      </HStack>
                      <HStack spacing={3} align='start'>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                          flexShrink={0}
                          mt={0.5}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                          wordBreak='break-word'
                          flex={1}
                        >
                          {t('clubAppPage.platform.features.eventScheduling')}
                        </Text>
                      </HStack>
                      <HStack spacing={3} align='start'>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                          flexShrink={0}
                          mt={0.5}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                          wordBreak='break-word'
                          flex={1}
                        >
                          {t('clubAppPage.platform.features.socialFeatures')}
                        </Text>
                      </HStack>
                      <HStack spacing={3} align='start'>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                          flexShrink={0}
                          mt={0.5}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                          wordBreak='break-word'
                          flex={1}
                        >
                          {t('clubAppPage.platform.features.revenueTracking')}
                        </Text>
                      </HStack>
                      <HStack spacing={3} align='start'>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                          flexShrink={0}
                          mt={0.5}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                          wordBreak='break-word'
                          flex={1}
                        >
                          {t(
                            'clubAppPage.platform.features.realTimeNotifications'
                          )}
                        </Text>
                      </HStack>
                      <HStack spacing={3} align='start'>
                        <Icon
                          as={FaCheck}
                          color='green.500'
                          boxSize={{ base: 4, md: 5 }}
                          flexShrink={0}
                          mt={0.5}
                        />
                        <Text
                          fontSize={{ base: 'xs', md: 'sm' }}
                          fontWeight='500'
                          color={useColorModeValue('gray.700', 'gray.300')}
                          wordBreak='break-word'
                          flex={1}
                        >
                          {t(
                            'clubAppPage.platform.features.analyticsDashboard'
                          )}
                        </Text>
                      </HStack>
                    </SimpleGrid>
                  </VStack>

                  <Stack
                    direction={{ base: 'column', sm: 'row' }}
                    spacing={4}
                    w='full'
                  >
                    <Button
                      as='a'
                      href={demoConfig.clubApp}
                      target='_blank'
                      rel='noopener noreferrer'
                      size='lg'
                      colorScheme='orange'
                      rightIcon={<FaPlay />}
                      flex={1}
                      borderRadius='xl'
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'xl',
                      }}
                      transition='all 0.3s'
                    >
                      {t('clubAppPage.platform.buttons.tryDemo')}
                    </Button>
                    <Button
                      as={RouterLink}
                      to='/contact'
                      size='lg'
                      variant='outline'
                      colorScheme='orange'
                      rightIcon={<FaDownload />}
                      flex={1}
                      borderRadius='xl'
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'lg',
                      }}
                      transition='all 0.3s'
                    >
                      {t('clubAppPage.platform.buttons.getStarted')}
                    </Button>
                  </Stack>
                </VStack>
              </Box>

              <HStack
                spacing={{ base: 4, md: 6, lg: 8 }}
                align='center'
                justify='center'
                flexWrap={{ base: 'wrap', lg: 'nowrap' }}
                display={{ base: 'none', lg: 'flex' }}
                w='full'
              >
                <Box
                  position='relative'
                  borderRadius='3xl'
                  overflow='hidden'
                  _hover={{
                    transform: 'scale(1.02)',
                    boxShadow: '4xl',
                  }}
                  transition='all 0.3s'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  minH={{ base: '400px', md: '500px' }}
                  boxShadow='2xl'
                  flex='1'
                  maxW={{ base: '250px', xl: '300px' }}
                >
                  <Image
                    src='/images/clubApp/club.png'
                    alt='ClubApp Main Interface'
                    maxH={{ base: '500px', md: '680px' }}
                    w='100%'
                    h='auto'
                    objectFit='contain'
                    borderRadius='2xl'
                  />
                </Box>

                <Box
                  position='relative'
                  borderRadius='3xl'
                  overflow='hidden'
                  _hover={{
                    transform: 'scale(1.02)',
                    boxShadow: '4xl',
                  }}
                  transition='all 0.3s'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  minH={{ base: '400px', md: '500px' }}
                  boxShadow='2xl'
                  flex='1'
                  maxW={{ base: '250px', xl: '300px' }}
                >
                  <Image
                    src='/images/clubApp/club-lobby-filter.png'
                    alt='ClubApp Lobby Filter'
                    maxH={{ base: '500px', md: '680px' }}
                    w='100%'
                    h='auto'
                    objectFit='contain'
                    borderRadius='2xl'
                  />
                </Box>
              </HStack>
            </Grid>

            <Box id='app-features' w='full'>
              <VStack spacing={12} align='center'>
                <VStack spacing={4} textAlign='center'>
                  <Heading
                    size='xl'
                    color={useColorModeValue('gray.800', 'white')}
                    wordBreak='break-word'
                  >
                    {t('clubAppPage.appFeatures.title')}
                  </Heading>
                  <Text
                    fontSize='lg'
                    color='gray.500'
                    maxW='2xl'
                    wordBreak='break-word'
                  >
                    {t('clubAppPage.appFeatures.subtitle')}
                  </Text>
                </VStack>

                {/* Desktop Features Section */}
                <Box w='full' display={{ base: 'none', md: 'block' }}>
                  <SimpleGrid
                    columns={{ md: 2, xl: 4 }}
                    columnGap={{ md: 4, xl: 6 }}
                    rowGap={{ md: 8, xl: 6 }}
                    w='full'
                  >
                    {FEATURES.map(feature => {
                      const {
                        id,
                        icon,
                        colorScheme,
                        features,
                        featureIcons,
                        image,
                      } = feature;
                      return (
                        <VStack
                          key={id}
                          spacing={{ md: 2, xl: 6 }}
                          align='stretch'
                        >
                          <VStack
                            p={4}
                            bg={useColorModeValue('white', 'gray.800')}
                            borderRadius='xl'
                            boxShadow='md'
                            border='1px solid'
                            borderColor={useColorModeValue(
                              'gray.200',
                              'gray.700'
                            )}
                            _hover={{
                              transform: 'translateY(-2px)',
                              boxShadow: 'lg',
                            }}
                            transition='all 0.3s ease'
                            align='start'
                            mx='auto'
                            w='full'
                            maxW={{ md: '280px', xl: 'full' }}
                          >
                            <HStack spacing={2} w='full'>
                              <Box
                                p={2}
                                bg={useColorModeValue(
                                  `${colorScheme}.100`,
                                  `${colorScheme}.200`
                                )}
                                borderRadius='lg'
                                color={useColorModeValue(
                                  `${colorScheme}.600`,
                                  `${colorScheme}.800`
                                )}
                              >
                                <Icon as={icon} boxSize={5} />
                              </Box>
                              <VStack align='start' spacing={0} flex={1}>
                                <Heading
                                  size='sm'
                                  color={useColorModeValue('gray.800', 'white')}
                                  fontWeight='600'
                                  wordBreak='break-word'
                                >
                                  {t(`clubAppPage.appFeatures.${id}.title`)}
                                </Heading>
                              </VStack>
                            </HStack>

                            <Text
                              fontSize='xs'
                              color={useColorModeValue('gray.600', 'gray.300')}
                              lineHeight='base'
                              wordBreak='break-word'
                            >
                              {t(`clubAppPage.appFeatures.${id}.subtitle`)}
                            </Text>

                            <VStack spacing={1} align='start' w='full'>
                              {features.map((featureKey, index) => (
                                <HStack
                                  key={featureKey}
                                  spacing={2}
                                  align='start'
                                >
                                  <Icon
                                    as={featureIcons[index]}
                                    color={`${colorScheme}.500`}
                                    boxSize={3}
                                    flexShrink={0}
                                    mt={0.5}
                                  />
                                  <Text
                                    fontSize='xs'
                                    color={useColorModeValue(
                                      'gray.700',
                                      'gray.300'
                                    )}
                                    fontWeight='500'
                                    wordBreak='break-word'
                                    flex={1}
                                  >
                                    {t(
                                      `clubAppPage.appFeatures.${id}.features.${featureKey}`
                                    )}
                                  </Text>
                                </HStack>
                              ))}
                            </VStack>
                          </VStack>

                          <Box
                            position='relative'
                            borderRadius='xl'
                            overflow='hidden'
                            _hover={{
                              transform: 'scale(1.02)',
                              boxShadow: '2xl',
                            }}
                            transition='all 0.3s'
                            boxShadow='lg'
                            mx='auto'
                            w='full'
                            maxW={{ md: '280px', xl: 'full' }}
                          >
                            <Image
                              src={image}
                              alt={`${t(`clubAppPage.appFeatures.${id}.title`)} Interface`}
                              w='full'
                              h='auto'
                              objectFit='contain'
                              borderRadius='lg'
                            />
                          </Box>
                        </VStack>
                      );
                    })}
                  </SimpleGrid>
                </Box>

                {/* Mobile-only carousel */}
                <Box w='full' display={{ base: 'block', md: 'none' }}>
                  <HStack
                    spacing={4}
                    overflowX='auto'
                    px={2}
                    py={2}
                    align='stretch'
                    sx={{
                      scrollSnapType: 'x mandatory',
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}
                  >
                    {FEATURES.map(feature => (
                      <MobileFeatureCard
                        key={feature.id}
                        feature={feature}
                        t={t}
                        useColorModeValue={useColorModeValue}
                      />
                    ))}
                  </HStack>
                </Box>
              </VStack>
            </Box>
          </VStack>

          {/* Benefits Section */}
          <Box
            bg={useColorModeValue('white', 'gray.800')}
            borderRadius='3xl'
            p={{ base: 8, md: 12 }}
            mt={20}
            w='full'
            maxW='6xl'
            boxShadow='2xl'
            border='1px solid'
            borderColor={useColorModeValue('gray.200', 'gray.700')}
          >
            <VStack spacing={8}>
              <VStack spacing={4} textAlign='center'>
                <Heading
                  size='xl'
                  color={useColorModeValue('gray.800', 'white')}
                  wordBreak='break-word'
                >
                  {t('clubAppPage.benefits.title')}
                </Heading>
                <Text
                  fontSize='lg'
                  color='gray.500'
                  maxW='2xl'
                  wordBreak='break-word'
                >
                  {t('clubAppPage.benefits.subtitle')}
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w='full'>
                <VStack spacing={4} textAlign='center'>
                  <Box
                    p={4}
                    bg={useColorModeValue('purple.100', 'purple.200')}
                    borderRadius='2xl'
                    color={useColorModeValue('purple.600', 'purple.800')}
                  >
                    <Icon as={FaUsers} boxSize={8} />
                  </Box>
                  <Heading
                    size='md'
                    color={useColorModeValue('gray.800', 'white')}
                    wordBreak='break-word'
                  >
                    {t('clubAppPage.benefits.memberEngagement.title')}
                  </Heading>
                  <Text
                    fontSize='sm'
                    color={useColorModeValue('gray.600', 'gray.300')}
                    textAlign='center'
                    wordBreak='break-word'
                  >
                    {t('clubAppPage.benefits.memberEngagement.description')}
                  </Text>
                </VStack>

                <VStack spacing={4} textAlign='center'>
                  <Box
                    p={4}
                    bg={useColorModeValue('blue.100', 'blue.200')}
                    borderRadius='2xl'
                    color={useColorModeValue('blue.600', 'blue.800')}
                  >
                    <Icon as={FaChartLine} boxSize={8} />
                  </Box>
                  <Heading
                    size='md'
                    color={useColorModeValue('gray.800', 'white')}
                    wordBreak='break-word'
                  >
                    {t('clubAppPage.benefits.businessIntelligence.title')}
                  </Heading>
                  <Text
                    fontSize='sm'
                    color={useColorModeValue('gray.600', 'gray.300')}
                    textAlign='center'
                    wordBreak='break-word'
                  >
                    {t('clubAppPage.benefits.businessIntelligence.description')}
                  </Text>
                </VStack>

                <VStack spacing={4} textAlign='center'>
                  <Box
                    p={4}
                    bg={useColorModeValue('green.100', 'green.200')}
                    borderRadius='2xl'
                    color={useColorModeValue('green.600', 'green.800')}
                  >
                    <Icon as={FaCog} boxSize={8} />
                  </Box>
                  <Heading
                    size='md'
                    color={useColorModeValue('gray.800', 'white')}
                    wordBreak='break-word'
                  >
                    {t('clubAppPage.benefits.easyManagement.title')}
                  </Heading>
                  <Text
                    fontSize='sm'
                    color={useColorModeValue('gray.600', 'gray.300')}
                    textAlign='center'
                    wordBreak='break-word'
                  >
                    {t('clubAppPage.benefits.easyManagement.description')}
                  </Text>
                </VStack>
              </SimpleGrid>
            </VStack>
          </Box>

          {/* CTA Section */}
          <Box
            bg='linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)'
            color='white'
            borderRadius='3xl'
            p={{ base: 8, md: 12 }}
            mt={16}
            w='full'
            maxW='4xl'
            textAlign='center'
            boxShadow='2xl'
          >
            <VStack spacing={6}>
              <Heading size='xl' wordBreak='break-word'>
                {t('clubAppPage.cta.title')}
              </Heading>
              <Text
                fontSize='lg'
                opacity='0.9'
                maxW='2xl'
                wordBreak='break-word'
              >
                {t('clubAppPage.cta.subtitle')}
              </Text>
              <Stack
                direction={{ base: 'column', sm: 'row' }}
                spacing={4}
                justify='center'
              >
                <Button
                  as={RouterLink}
                  to='/contact'
                  size='lg'
                  variant='solid'
                  bg='white'
                  color='orange.500'
                  _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                  borderRadius='xl'
                >
                  {t('clubAppPage.cta.buttons.getStarted')}
                </Button>
                <Button
                  as={RouterLink}
                  to='/demo'
                  size='lg'
                  variant='outline'
                  borderColor='white'
                  color='white'
                  _hover={{
                    bg: 'whiteAlpha.200',
                    transform: 'translateY(-2px)',
                  }}
                  borderRadius='xl'
                >
                  {t('clubAppPage.cta.buttons.scheduleDemo')}
                </Button>
              </Stack>
            </VStack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ClubAppPage;
